import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { authService, userService } from '../../../../services/apiService';
import { getDisplayName, getProfileDisplayEmail, getProfileDisplayName, getProfileDisplayPhone } from '../../../../utils/displayName';
import { FaUser, FaEnvelope, FaLock, FaPhone, FaMapMarkerAlt, FaCamera, FaSave, FaEdit, FaTimes, FaTools, FaCertificate, FaBuilding } from 'react-icons/fa';
import { toast } from 'react-toastify';

const EMPTY_PASSWORD_FIELDS = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

function formatGarageServices(services) {
  if (Array.isArray(services)) return services.join(', ');
  return String(services || '').trim();
}

function unwrapProfileUser(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.user && typeof raw.user === 'object' && !raw.role && !raw.garageDetails && !raw.email && !raw.phone) {
    return raw.user;
  }
  return raw;
}

function mapUserToFormData(user) {
  const profileUser = unwrapProfileUser(user) || {};
  const garageDetails = profileUser?.garageDetails || {};
  const address = profileUser?.address || {};
  const fullAddress = address.fullAddress || [
    address.area,
    address.city,
    address.state,
    address.country,
    address.postalCode
  ].filter(Boolean).join(', ');

  return {
    name:
      getProfileDisplayName(profileUser) ||
      garageDetails.ownerName ||
      garageDetails.displayName ||
      garageDetails.garageName ||
      '',
    email: getProfileDisplayEmail(profileUser) || garageDetails.email || '',
    phone: getProfileDisplayPhone(profileUser) || garageDetails.supportPhone || '',
    address: fullAddress,
    bio: garageDetails.description || profileUser?.bio || '',
    profilePhoto: profileUser?.avatar || garageDetails.profilePhoto || 'https://via.placeholder.com/150',
    garageName: garageDetails.garageName || '',
    gstNumber: garageDetails.gstNumber || '',
    panNumber: garageDetails.panNumber || '',
    businessAddress: garageDetails.businessAddress || fullAddress,
    services: formatGarageServices(garageDetails.services),
    certifications: garageDetails.certifications || '',
    garageId: garageDetails.garageId || String(profileUser?._id || profileUser?.id || ''),
    ...EMPTY_PASSWORD_FIELDS
  };
}

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    bio: '',
    profilePhoto: '',
    garageName: '',
    gstNumber: '',
    panNumber: '',
    businessAddress: '',
    services: '',
    certifications: '',
    garageId: '',
    ...EMPTY_PASSWORD_FIELDS
  });
  const [loadedSnapshot, setLoadedSnapshot] = useState(null);

  const applyProfileUser = useCallback((profileUser, existingUser = null) => {
    const base = unwrapProfileUser(existingUser || user) || existingUser || user || null;
    const incoming = unwrapProfileUser(profileUser) || profileUser || null;
    const merged = {
      ...(base || {}),
      ...(incoming || {}),
      role: incoming?.role || base?.role
    };
    const mapped = mapUserToFormData(merged);
    setFormData(mapped);
    setLoadedSnapshot(mapped);
    updateUser?.(merged);
  }, [updateUser, user]);

  useEffect(() => {
    let mounted = true;

    const loadProfile = async () => {
      setLoadingProfile(true);
      try {
        const res = await authService.getProfile();
        const profileUser = unwrapProfileUser(
          res?.user || res?.data?.user || res?.data?.data?.user || res?.data
        );
        if (!mounted) return;
        if (profileUser && typeof profileUser === 'object') {
          applyProfileUser(profileUser, user);
          return;
        }
        if (user) applyProfileUser(user, user);
      } catch (err) {
        if (user) applyProfileUser(user, user);
        toast.error(err.message || 'Failed to load profile');
      } finally {
        if (mounted) setLoadingProfile(false);
      }
    };

    loadProfile();
    return () => {
      mounted = false;
    };
    // Load full profile from backend once when page opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          profilePhoto: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const userId = user?._id || user?.id;
    if (!userId) {
      toast.error('User not found');
      return;
    }

    if (formData.newPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }
      if (formData.newPassword.length < 6) {
        toast.error('Password must be at least 6 characters long');
        return;
      }
    }

    setSavingProfile(true);
    try {
      const existingGarageDetails = user?.garageDetails || {};
      const existingAddress = user?.address || {};
      const garageDetails = {
        ...existingGarageDetails,
        garageName: formData.garageName.trim(),
        ownerName: formData.name.trim(),
        displayName: formData.name.trim(),
        email: formData.email.trim(),
        supportPhone: (formData.phone || '').replace(/\D/g, '').slice(-10),
        description: formData.bio.trim(),
        services: formData.services.trim(),
        gstNumber: formData.gstNumber.trim(),
        panNumber: formData.panNumber.trim(),
        businessAddress: formData.businessAddress.trim(),
        certifications: formData.certifications.trim(),
        garageId: formData.garageId.trim(),
        profilePhoto: formData.profilePhoto
      };

      const res = await userService.updateUser(userId, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: (formData.phone || '').replace(/\D/g, '').slice(-10),
        address: {
          ...existingAddress,
          fullAddress: formData.address.trim() || formData.businessAddress.trim()
        },
        garageDetails
      });

      const updated = res?.user || res?.data?.user || res;
      if (updated) {
        applyProfileUser(
          {
            ...user,
            ...updated,
            role: updated.role || user?.role,
            garageDetails: updated.garageDetails || garageDetails
          },
          user
        );
      }

      if (formData.newPassword) {
        await authService.changePassword(formData.currentPassword, formData.newPassword);
      }

      toast.success('Profile updated successfully');
      setIsEditing(false);
      setFormData((prev) => ({
        ...prev,
        ...EMPTY_PASSWORD_FIELDS
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (loadedSnapshot) {
      setFormData({
        ...loadedSnapshot,
        ...EMPTY_PASSWORD_FIELDS
      });
    }
  };

  if (loadingProfile) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-600">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">My Profile</h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="btn-primary flex items-center gap-2"
          >
            <FaEdit /> Edit Profile
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                <img loading="lazy"
                  src={formData.profilePhoto || 'https://via.placeholder.com/150'}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                />
                {isEditing && (
                  <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition">
                    <FaCamera className="text-sm" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <h4 className="text-lg font-bold text-gray-900">{formData.name || getDisplayName(user) || ''}</h4>
              {formData.email ? <p className="text-sm text-gray-600">{formData.email}</p> : null}
              <span className="mt-2 px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                Garage
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaUser className="inline mr-2" /> Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaEnvelope className="inline mr-2" /> Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaPhone className="inline mr-2" /> Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder=""
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaMapMarkerAlt className="inline mr-2" /> Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="Enter your address"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaTools className="text-purple-500" /> Garage Information
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Garage Name *
                  </label>
                  <input
                    type="text"
                    name="garageName"
                    value={formData.garageName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="My Auto Garage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Garage ID
                  </label>
                  <input
                    type="text"
                    name="garageId"
                    value={formData.garageId}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="GAR001"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GST Number
                    </label>
                    <input
                      type="text"
                      name="gstNumber"
                      value={formData.gstNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      placeholder="GST123456789"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PAN Number
                    </label>
                    <input
                      type="text"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      placeholder="ABCDE1234F"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaBuilding className="inline mr-2" /> Business Address
                  </label>
                  <textarea
                    name="businessAddress"
                    value={formData.businessAddress}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="Enter business address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Services Offered
                  </label>
                  <textarea
                    name="services"
                    value={formData.services}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="e.g., Engine Repair, Brake Service, AC Service, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaCertificate className="inline mr-2" /> Certifications
                  </label>
                  <textarea
                    name="certifications"
                    value={formData.certifications}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="e.g., ASE Certified, Authorized Service Center, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">About / Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="Tell customers about your garage"
                  />
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaLock className="inline mr-2" /> Current Password
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter current password (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaLock className="inline mr-2" /> New Password
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter new password (min 6 characters)"
                      minLength={6}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaLock className="inline mr-2" /> Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Leave password fields empty if you don't want to change it</p>
                </div>
              </div>
            )}

            {isEditing && (
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center gap-2"
                  disabled={savingProfile}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                  disabled={savingProfile}
                >
                  <FaSave /> {savingProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
